import { UseQueryResult, useQuery } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";
import { IUserRole } from "../../services/user.service";
import { useUserStore } from "../../stores/user.store";
import { AUTH_CONSTANTS } from "../../utils/auth";

/**
 * React Query cache key for the logged-in user's own record. Shared by every
 * caller so all mount points dedupe onto a single request.
 */
export const CURRENT_USER_QUERY_KEY = "current-user-info";

export interface UseCurrentUserOptions {
	/**
	 * Mount behaviour, forwarded to React Query.
	 *
	 * Leave unset for the session-wide default (respect `staleTime`, so mounting
	 * is free). Pass `"always"` from a screen whose correctness depends on an
	 * exact assigned-program list, to revalidate once per entry to that screen.
	 */
	refetchOnMount?: boolean | "always";
}

/**
 * Keeps the logged-in user's record — crucially `userInfo.programs`, the
 * assigned-program scope most screens filter by — current for the whole
 * session, and writes the result back into `useUserStore`.
 *
 * SIMS-7884. `userInfo` was previously a login-time constant: App.tsx fetched
 * it once inside its Auth0 bootstrap effect (guarded by `initializingRef`) and
 * nothing re-fetched it afterwards, while zustand's `persist` kept the snapshot
 * in localStorage across reloads. The existing `isUserDataStale` /
 * `lastFetchedAt` staleness check therefore only ever ran at bootstrap, so a
 * program assigned mid-session did not appear until the user signed out and
 * back in.
 *
 * Writing back into the store rather than returning the data to callers is
 * intentional: the ~30 components that already read `userInfo` from the store
 * get the fresh value through their existing subscription, with no change to
 * how any of them obtain or scope it.
 */
export const useCurrentUser = ({
	refetchOnMount,
}: UseCurrentUserOptions = {}): UseQueryResult<IUserRole, Error> => {
	const { isAuthenticated, user } = useAuth0();
	const email = user?.email;
	const subIdp = user?.sub;

	const refreshUserInfo = useUserStore((state) => state.refreshUserInfo);
	const userInfo = useUserStore((state) => state.userInfo);
	const lastFetchedAt = useUserStore((state) => state.lastFetchedAt);

	return useQuery<IUserRole, Error>({
		queryKey: [CURRENT_USER_QUERY_KEY, email, subIdp],
		queryFn: () => refreshUserInfo(email as string, subIdp as string),
		enabled: Boolean(isAuthenticated && email && subIdp),
		// Seed the cache with the snapshot the bootstrap already fetched, dated
		// with the timestamp it was fetched at. Without this, mounting the hook
		// would fire a duplicate /users/user-info call on every login, because
		// React Query has no way to know the store's snapshot is seconds old.
		initialData: userInfo ?? undefined,
		initialDataUpdatedAt: lastFetchedAt ?? undefined,
		// Same 5-minute window App.tsx bootstrap already applies via
		// isUserDataStale — one definition of "stale user data", two consumers.
		staleTime: AUTH_CONSTANTS.USER_DATA_STALE_TIME,
		refetchOnMount,
		// Best-effort: the store keeps the last known good snapshot, so a failed
		// revalidation must not retry-storm the endpoint behind the user's back.
		retry: false,
	});
};
